import { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast, Toaster } from "sonner";
import { FileText, Sheet, Trash2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Settlements = () => {
  const [items, setItems] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [teacherId, setTeacherId] = useState("");
  const [period, setPeriod] = useState(new Date().toISOString().substring(0, 7));
  const [payoutMethod, setPayoutMethod] = useState("twint");
  const [preview, setPreview] = useState(null);

  const load = () => api.get("/admin/settlements").then(r => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/admin/teachers").then(r => setTeachers(r.data));
  }, []);

  const doPreview = async () => {
    if (!teacherId || !period) return;
    const r = await api.get(`/admin/settlements/preview?teacher_id=${teacherId}&period=${period}`);
    setPreview(r.data);
  };
  const doCreate = async () => {
    try {
      await api.post(`/admin/settlements/create?teacher_id=${teacherId}&period=${period}&payout_method=${payoutMethod}`);
      toast.success("Abrechnung erstellt");
      setPreview(null);
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Unbekannter Fehler";
      toast.error(`Fehler: ${detail}`);
    }
  };
  const remove = async (it) => {
    if (!window.confirm("Löschen?")) return;
    await api.delete(`/admin/settlements/${it.id}`);
    load();
  };
  const download = async (sid, kind, variant) => {
    const token = localStorage.getItem("kk_token");
    const url = variant
      ? `${BACKEND_URL}/api/admin/settlements/${sid}/export.${kind}?variant=${variant}`
      : `${BACKEND_URL}/api/admin/settlements/${sid}/export.${kind}`;
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await res.blob();
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objUrl; a.download = `abrechnung_${variant || kind}.${kind}`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(objUrl);
  };

  return (
    <div className="space-y-6" data-testid="admin-settlements">
      <Toaster position="top-center" theme="light" />
      <h1 className="font-serif-display text-3xl">Abrechnungen</h1>

      <div className="card-academy rounded-lg p-5 grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-muted-foreground block mb-2">Lehrperson</label>
          <select value={teacherId} onChange={e => setTeacherId(e.target.value)} data-testid="settlement-teacher-select" className="w-full bg-input/30 border border-border rounded-md px-3 py-2 text-sm">
            <option value="">–</option>
            {teachers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="text-xs uppercase tracking-[0.2em] text-muted-foreground block mb-2">Zeitraum (YYYY-MM, YYYY-Q1 oder YYYY-MM..YYYY-MM)</label>
          <input value={period} onChange={e => setPeriod(e.target.value)} placeholder="z.B. 2026-05 oder 2026-01..2026-05" className="w-full bg-input/30 border border-border rounded-md px-3 py-2 text-sm" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-muted-foreground block mb-2">Auszahlungsart</label>
          <select value={payoutMethod} onChange={e => setPayoutMethod(e.target.value)} data-testid="settlement-payout-method" className="w-full bg-input/30 border border-border rounded-md px-3 py-2 text-sm">
            <option value="twint">TWINT</option>
            <option value="cash">Barzahlung</option>
            <option value="bank">Banküberweisung</option>
          </select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button onClick={doPreview} data-testid="settlement-preview-btn" className="btn-outline-gold rounded-md px-4 py-2 text-sm">Vorschau</button>
          <button onClick={doCreate} disabled={!preview} data-testid="settlement-create-btn" className="btn-gold rounded-md px-4 py-2 text-sm disabled:opacity-50">Erstellen</button>
        </div>
      </div>

      {preview && (
        <div className="card-academy rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="font-serif-display text-xl">Vorschau – {preview.teacher.name} · {preview.period}</div>
          </div>
          {preview.diagnostics && preview.diagnostics.length > 0 && (
            <div className="mb-4 p-4 bg-amber-50 dark:bg-amber-950/20 border border-amber-300/50 rounded-md" data-testid="settlement-diagnostics">
              <div className="text-sm font-medium text-amber-800 dark:text-amber-300 mb-2">⚠️ Warum die Abrechnung CHF 0.00 zeigt:</div>
              <ul className="text-sm text-amber-900 dark:text-amber-200 space-y-1 list-disc list-inside">
                {preview.diagnostics.map((d, i) => <li key={i}>{d}</li>)}
              </ul>
            </div>
          )}
          <table className="w-full text-sm">
            <thead><tr className="text-xs uppercase tracking-[0.15em] text-muted-foreground border-b border-border">{["Kurs", "Termine", "Lektionen", "Schüler", "Teilnahmen", "Brutto", "Lehrer", "Academy"].map(h => <th key={h} className="text-left py-2">{h}</th>)}</tr></thead>
            <tbody>
              {preview.items.map(it => (
                <tr key={it.course_id} className="border-b border-border/50">
                  <td className="py-2">{it.course_name}</td>
                  <td className="py-2">{it.sessions}{it.lessons_per_session > 1 ? ` × ${it.lessons_per_session}` : ""}</td>
                  <td className="py-2">{it.lessons}</td>
                  <td className="py-2">{it.students}</td>
                  <td className="py-2">{it.participations}</td>
                  <td className="py-2">CHF {it.gross.toFixed(2)}</td>
                  <td className="py-2">CHF {it.teacher.toFixed(2)}</td>
                  <td className="py-2">CHF {it.academy.toFixed(2)}</td>
                </tr>
              ))}
              <tr className="font-semibold">
                <td className="py-3">TOTAL</td><td></td><td></td><td></td><td></td>
                <td>CHF {preview.gross.toFixed(2)}</td>
                <td>CHF {preview.teacher_share.toFixed(2)}</td>
                <td className="text-primary">CHF {preview.academy_share.toFixed(2)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <div className="card-academy rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40 border-b border-border">
              <tr>{["Lehrperson", "Zeitraum", "Brutto", "Lehrer", "Academy", "Auszahlung", "Status", "Export", ""].map(h => <th key={h} className="text-left text-xs uppercase tracking-[0.15em] text-muted-foreground px-4 py-3">{h}</th>)}</tr>
            </thead>
            <tbody>
              {items.map(s => {
                const pmLabel = { twint: "TWINT", cash: "Bar", bank: "Bank" }[s.payout_method] || "—";
                return (
                <tr key={s.id} className="border-b border-border/50">
                  <td className="px-4 py-3">{s.teacher_name}</td>
                  <td className="px-4 py-3">{s.period}</td>
                  <td className="px-4 py-3">CHF {s.gross.toFixed(2)}</td>
                  <td className="px-4 py-3">CHF {s.teacher_share.toFixed(2)}</td>
                  <td className="px-4 py-3 text-primary">CHF {s.academy_share.toFixed(2)}</td>
                  <td className="px-4 py-3"><span className="inline-flex items-center text-xs uppercase tracking-[0.15em] px-2 py-1 rounded-full border border-primary/30 text-primary bg-primary/5">{pmLabel}</span></td>
                  <td className="px-4 py-3 capitalize">{s.status}</td>
                  <td className="px-4 py-3 flex gap-2 flex-wrap">
                    <button onClick={() => download(s.id, "pdf", "teacher")} data-testid={`settlement-pdf-teacher-${s.id}`} className="text-primary hover:opacity-70 inline-flex items-center gap-1 text-xs font-medium" title="PDF für Lehrperson (ohne Brutto/Academy, mit Schülerliste)"><FileText className="w-3.5 h-3.5" />Lehrer-PDF</button>
                    <button onClick={() => download(s.id, "pdf", "admin")} data-testid={`settlement-pdf-admin-${s.id}`} className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-xs" title="PDF intern (mit allen Beträgen)"><FileText className="w-3.5 h-3.5" />Admin-PDF</button>
                    <button onClick={() => download(s.id, "xlsx")} data-testid={`settlement-xlsx-${s.id}`} className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-xs"><Sheet className="w-3.5 h-3.5" />Excel</button>
                  </td>
                  <td className="px-4 py-3"><button onClick={() => remove(s)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button></td>
                </tr>
              );})}
              {items.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-muted-foreground">Keine Abrechnungen</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Settlements;
